import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BankStatements() {
  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  useEffect(() => { loadStatements(); }, []);

  const loadStatements = async () => {
    try {
      const { data } = await axios.get(`${API}/bank-statements`, { withCredentials: true });
      setStatements(data);
    } catch (error) {
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await axios.post(`${API}/bank-statements/upload`, data, { withCredentials: true });
      toast.success(`Imported ${response.data.transaction_count} transactions`);
      setSelectedStatement(response.data);
      setShowDialog(false);
      setFile(null);
      loadStatements();
    } catch (error) {
      toast.error('Failed to upload statement');
    } finally {
      setUploading(false);
    }
  };

  const viewStatement = async (id) => {
    try {
      const { data } = await axios.get(`${API}/bank-statements/${id}`, { withCredentials: true });
      setSelectedStatement(data);
    } catch (error) {
      toast.error('Failed to load statement');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="bank-statements-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="bank-statements-title">Bank Statements</h1>
          <p className="mt-2 text-zinc-600">Import and reconcile bank transactions</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="upload-statement-button">
              <Upload size={18} /> Upload Statement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Upload Bank Statement</DialogTitle></DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Statement File (CSV or PDF)</Label>
                <Input type="file" accept=".csv,.pdf" onChange={(e) => setFile(e.target.files[0])} required data-testid="statement-file-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                <p className="text-xs text-zinc-500 mt-2">Supports CSV and PDF formats</p>
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-statement-button">
                {uploading ? 'Processing...' : 'Upload & Reconcile'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-zinc-200">
          <div className="p-6 border-b border-zinc-200">
            <h2 className="text-xl font-semibold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Statements</h2>
          </div>
          <div className="divide-y divide-zinc-200" data-testid="statements-list">
            {statements.length === 0 ? (
              <div className="p-6 text-center text-zinc-500" data-testid="no-statements">No statements yet</div>
            ) : statements.map((s) => (
              <button key={s.id} onClick={() => viewStatement(s.id)} className="w-full p-4 text-left hover:bg-zinc-50 transition-colors flex items-start gap-3" data-testid={`statement-${s.id}`}>
                <FileText size={20} className="text-zinc-400 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{s.filename}</p>
                  <p className="text-xs text-zinc-500">{s.transaction_count} transactions</p>
                  <p className="text-xs text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="p-6 border-b border-zinc-200">
            <h2 className="text-xl font-semibold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              {selectedStatement ? `${selectedStatement.filename} - Reconciliation` : 'Reconciliation Report'}
            </h2>
          </div>
          
          {selectedStatement ? (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-green-200 bg-green-50" data-testid="matched-count">
                  <p className="text-xs font-bold uppercase tracking-wider text-green-700">Matched</p>
                  <p className="text-2xl font-bold text-green-700 mt-1">{selectedStatement.reconciliation?.matched_count || 0}</p>
                </div>
                <div className="p-4 border border-yellow-200 bg-yellow-50" data-testid="unmatched-count">
                  <p className="text-xs font-bold uppercase tracking-wider text-yellow-700">Unmatched</p>
                  <p className="text-2xl font-bold text-yellow-700 mt-1">{selectedStatement.reconciliation?.unmatched_count || 0}</p>
                </div>
              </div>

              {selectedStatement.reconciliation?.matched?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-600 mb-3">Matched Transactions</h3>
                  <div className="border border-zinc-200">
                    {selectedStatement.reconciliation.matched.slice(0, 10).map((tx, i) => (
                      <div key={i} className="p-3 border-b border-zinc-200 last:border-b-0 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-900 truncate">{tx.description}</p>
                          <p className="text-xs text-zinc-500">{tx.date} • Matched to {tx.match.type}: {tx.match.ref}</p>
                        </div>
                        <p className="text-sm font-semibold text-zinc-900">R {tx.amount.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedStatement.reconciliation?.unmatched?.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-600 mb-3">Unmatched Transactions</h3>
                  <div className="border border-zinc-200">
                    {selectedStatement.reconciliation.unmatched.slice(0, 10).map((tx, i) => (
                      <div key={i} className="p-3 border-b border-zinc-200 last:border-b-0 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-zinc-900 truncate">{tx.description}</p>
                          <p className="text-xs text-zinc-500">{tx.date}</p>
                        </div>
                        <p className="text-sm font-semibold text-zinc-900">R {tx.amount.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-12 text-center text-zinc-500" data-testid="no-statement-selected">
              Select a statement to view reconciliation
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
